import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import activityLog from '../services/activityLog';
import extensionBridge from '../services/extensionBridge';
import { apiErrorMessage } from '../utils/apiError';
import { Card, Chip, Input, Modal, PillButton } from '../ui';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as CheckIcon } from '../assets/icons/check.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import { ReactComponent as KeyIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as DownloadIcon } from '../assets/icons/download.svg';
import { ReactComponent as RefreshIcon } from '../assets/icons/refresh-cw.svg';
import { ReactComponent as AlertIcon } from '../assets/icons/alert-triangle.svg';
import './DeveloperSettings.css';

/*
 * Pestaña Developer (fase 4, design/SETTINGS-DEVELOPER-PRIVACY.md 5.1 y 5.3):
 *   - AuthorizationsCard: tokens personales de acceso (lista, crear con PIN, mostrar una vez, revocar,
 *     "Clear MCP authorizations"). Rutas: /api/account/tokens (solo con sesion).
 *   - ActivityLogCard: registro de actividad LOCAL (panel + extension). Nada de esto va al servidor.
 */

// Claves de i18n de cada scope. El id (items:read...) no se traduce: es lo que se pone en la API.
const SCOPE_KEYS = {
  'items:read': 'itemsRead',
  'items:write': 'itemsWrite',
  'totp:read': 'totpRead',
  'env:read': 'envRead',
  'secrets:reveal': 'secretsReveal',
  'ssh:sign': 'sshSign',
};
const EXPIRY_KEYS = { '12h': 'h12', '1d': 'd1', '7d': 'd7', '30d': 'd30', '90d': 'd90', '1y': 'y1' };
// Lo que propone `sgc signin` al abrir el panel (?create=cli): lo que hace falta para leer secretos y entornos, y 12 horas de vida.
const CLI_PREFILL = { name: 'CLI', clientKind: 'cli', scopes: ['items:read', 'env:read', 'secrets:reveal', 'totp:read'], expiresIn: '12h' };
const ENV_NAME_MAX = 128;
const CLIENT_KINDS = ['cli', 'sdk', 'mcp', 'other'];
// `other` (acceso directo a la API) cuelga del interruptor del SDK.
const integrationOf = (kind) => (kind === 'other' ? 'sdk' : kind);

function useFormatDate() {
  const { i18n } = useTranslation();
  return useCallback((value) => (value
    ? new Date(value).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
    : null), [i18n.language]);
}

function ConfirmModal({ open, title, body, confirmLabel, onConfirm, onClose, busy }) {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title={title}>
      <div className="sgc-dev__confirm">
        <p>{body}</p>
        <div className="sgc-dev__confirm-actions">
          <PillButton variant="secondary" onClick={onClose} disabled={busy}>{t('common.cancel')}</PillButton>
          <PillButton variant="danger" onClick={onConfirm} loading={busy}>{confirmLabel}</PillButton>
        </div>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Crear un token
 * ---------------------------------------------------------------------------------------------- */
function CreateTokenModal({ open, onClose, onCreated, integrations, meta, prefill }) {
  const { t } = useTranslation();
  const available = CLIENT_KINDS.filter((k) => integrations?.[integrationOf(k)]);
  const [name, setName] = useState('');
  const [clientKind, setClientKind] = useState(available[0] || 'sdk');
  const [scopes, setScopes] = useState(['items:read']);
  const [expiresIn, setExpiresIn] = useState('30d');
  const [envOnly, setEnvOnly] = useState(''); // limita env:read a un solo entorno (env:read:<nombre>)
  const [secret, setSecret] = useState('');
  const [security, setSecurity] = useState(null); // { hasPin, hasMasterPassword }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // { token, record }: solo en memoria, mientras el modal siga abierto
  const [copied, setCopied] = useState(false);

  // Cada apertura empieza limpia y consulta con que factor cuenta la cuenta.
  useEffect(() => {
    if (!open) return undefined;
    setName(prefill?.name || ''); setScopes(prefill?.scopes || ['items:read']); setExpiresIn(prefill?.expiresIn || '30d'); setSecret(''); setError(null);
    setBusy(false); setCreated(null); setCopied(false); setSecurity(null); setEnvOnly('');
    setClientKind(prefill?.clientKind && integrations?.[integrationOf(prefill.clientKind)] ? prefill.clientKind : (CLIENT_KINDS.find((k) => integrations?.[integrationOf(k)]) || 'sdk'));
    let cancelled = false;
    api.get('/account/security')
      .then((res) => { if (!cancelled) setSecurity(res.data); })
      .catch(() => { if (!cancelled) setSecurity({}); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleScope = (scope) => setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));

  // La caducidad de 12 horas es solo del cliente `cli` (el servidor la rechaza para los demas).
  const expiryOptions = useMemo(() => [...(clientKind === 'cli' ? ['12h'] : []), ...(meta?.expiryOptions || Object.keys(EXPIRY_KEYS).filter((o) => o !== '12h'))], [clientKind, meta]);
  useEffect(() => { if (!expiryOptions.includes(expiresIn)) setExpiresIn('30d'); }, [expiryOptions, expiresIn]);
  const envName = envOnly.trim();

  const noFactor = !!security && !security.hasPin && !security.hasMasterPassword;
  const canSubmit = !!security && !noFactor && name.trim() !== '' && scopes.length > 0 && secret !== '' && available.includes(clientKind) && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    // Un solo campo: se manda como PIN y/o contraseña maestra segun los que tenga la cuenta.
    // `env:read` + un nombre = `env:read:<nombre>`: el token solo lee ese entorno.
    const sentScopes = scopes.includes('env:read') && envName ? scopes.map((s) => (s === 'env:read' ? `env:read:${envName}` : s)) : scopes;
    const body = { name: name.trim(), clientKind, scopes: sentScopes, expiresIn };
    if (security?.hasPin) body.pin = secret;
    if (security?.hasMasterPassword) body.masterPassword = secret;
    try {
      const res = await api.post('/account/tokens', body);
      setCreated(res.data);
      setSecret('');
      onCreated?.();
    } catch (err) {
      const data = err.response?.data || {};
      if (data.code === 'second_factor_invalid') setError(t('account.danger.errInvalid', { remaining: data.remaining ?? 0 }));
      else if (data.code === 'too_many_attempts') setError(t('account.danger.errTooMany', { minutes: Math.max(1, Math.ceil((data.retryAfter || 60) / 60)) }));
      else if (data.code === 'second_factor_setup_needed') setError(t('account.danger.errSetup'));
      else setError(apiErrorMessage(err, t, 'settings.developer.auth.createError'));
      setSecret('');
      setBusy(false);
    }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* sin permiso de portapapeles: el token sigue visible para seleccionarlo */ }
  };

  const close = () => { setCreated(null); onClose(); };

  if (created) {
    return (
      <Modal open={open} onClose={close} title={t('settings.developer.auth.modal.tokenTitle')}>
        <div className="sgc-dev__created">
          <p className="sgc-dev__warning"><AlertIcon width={18} height={18} aria-hidden="true" /> {t('settings.developer.auth.modal.tokenWarning')}</p>
          <code className="sgc-dev__token" data-testid="new-token">{created.token}</code>
          <div className="sgc-dev__confirm-actions">
            <PillButton variant="secondary" icon={copied ? CheckIcon : CopyIcon} onClick={copyToken}>
              {copied ? t('settings.developer.auth.modal.copied') : t('settings.developer.auth.modal.copy')}
            </PillButton>
            <PillButton onClick={close}>{t('settings.developer.auth.modal.done')}</PillButton>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title={t('settings.developer.auth.modal.title')}>
      <form className="sgc-dev__form" onSubmit={submit}>
        <Input
          icon={KeyIcon}
          label={t('settings.developer.auth.modal.name')}
          value={name}
          maxLength={64}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />

        <label className="sgc-dev__field">
          <span className="sgc-dev__field-label">{t('settings.developer.auth.client')}</span>
          <select className="sgc-settings__select" value={clientKind} onChange={(e) => setClientKind(e.target.value)} data-testid="client-kind">
            {available.map((k) => <option key={k} value={k}>{t(`settings.developer.auth.clientName.${k}`)}</option>)}
          </select>
        </label>

        <fieldset className="sgc-dev__field sgc-dev__scopes">
          <legend className="sgc-dev__field-label">{t('settings.developer.auth.scopes')}</legend>
          {(meta?.scopes || Object.keys(SCOPE_KEYS)).map((scope) => (
            <label key={scope} className="sgc-dev__scope">
              <input type="checkbox" checked={scopes.includes(scope)} onChange={() => toggleScope(scope)} />
              <span>
                <code>{scope}</code>
                {(meta?.reservedScopes || []).includes(scope) && <Chip label={t('settings.developer.auth.modal.reserved')} />}
                <span className="sgc-dev__scope-desc">{t(`settings.developer.auth.scope.${SCOPE_KEYS[scope]}`)}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {scopes.includes('env:read') && (
          <Input
            label={t('settings.developer.auth.modal.envOnly')}
            value={envOnly}
            maxLength={ENV_NAME_MAX}
            onChange={(e) => setEnvOnly(e.target.value)}
            data-testid="env-only"
          />
        )}
        {scopes.includes('env:read') && <p className="sgc-settings__row-desc">{t('settings.developer.auth.modal.envOnlyHelp')}</p>}

        <label className="sgc-dev__field">
          <span className="sgc-dev__field-label">{t('settings.developer.auth.modal.expiry')}</span>
          <select className="sgc-settings__select" value={expiresIn} onChange={(e) => setExpiresIn(e.target.value)} data-testid="expiry">
            {expiryOptions.map((o) => <option key={o} value={o}>{t(`settings.developer.auth.expiry.${EXPIRY_KEYS[o]}`)}</option>)}
          </select>
        </label>

        {noFactor ? (
          <p className="sgc-dev__error">{t('account.danger.errSetup')}</p>
        ) : (
          <Input
            icon={KeyIcon}
            label={t('account.danger.secretLabel')}
            type="password"
            autoComplete="off"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
        )}
        {error && <p className="sgc-dev__error" role="alert">{error}</p>}

        <div className="sgc-dev__confirm-actions">
          <PillButton variant="secondary" onClick={onClose} disabled={busy}>{t('common.cancel')}</PillButton>
          <PillButton type="submit" icon={PlusIcon} disabled={!canSubmit} loading={busy}>{t('settings.developer.auth.modal.create')}</PillButton>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Autorizaciones
 * ---------------------------------------------------------------------------------------------- */
export function AuthorizationsCard({ integrations, refreshKey }) {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const [data, setData] = useState(null); // { tokens, scopes, reservedScopes, expiryOptions }
  const [error, setError] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [toRevoke, setToRevoke] = useState(null);
  const [clearMcp, setClearMcp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  // `sgc signin` abre el panel con ?create=cli: el modal sale solo, con lo que la CLI necesita ya marcado.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsCliToken = searchParams.get('create') === 'cli';

  const load = useCallback(() => {
    setError(false);
    return api.get('/account/tokens').then((res) => setData(res.data)).catch(() => setError(true));
  }, []);
  // Recarga cuando la pagina confirma un cambio de integraciones: la lista marca los tokens de las apagadas.
  useEffect(() => { load(); }, [load, refreshKey]);

  const anyIntegration = CLIENT_KINDS.some((k) => integrations?.[integrationOf(k)]);
  const cliReady = Boolean(integrations?.cli);
  useEffect(() => { if (wantsCliToken && data && cliReady) setShowCreate(true); }, [wantsCliToken, data, cliReady]);
  const closeCreate = () => {
    setShowCreate(false);
    if (wantsCliToken) { const next = new URLSearchParams(searchParams); next.delete('create'); setSearchParams(next, { replace: true }); }
    load();
  };
  const activeMcp = (data?.tokens || []).filter((x) => x.clientKind === 'mcp' && x.status === 'active').length;

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/account/tokens/${toRevoke.id}`);
      setNotice(t('settings.developer.auth.revoked'));
      setToRevoke(null);
      await load();
    } catch (err) {
      setNotice(apiErrorMessage(err, t, 'errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const revokeMcp = async () => {
    setBusy(true);
    try {
      await api.delete('/account/tokens', { params: { clientKind: 'mcp' } });
      setNotice(t('settings.developer.auth.clearedMcp'));
      setClearMcp(false);
      await load();
    } catch (err) {
      setNotice(apiErrorMessage(err, t, 'errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = (tok) => {
    if (tok.status === 'active' && !tok.integrationEnabled) return t('settings.developer.auth.status.integrationOff');
    return t(`settings.developer.auth.status.${tok.status}`);
  };

  return (
    <Card className="sgc-settings__card" data-testid="authorizations">
      <h2 className="sgc-settings__card-title">{t('settings.developer.auth.title')}</h2>
      <p className="sgc-settings__card-desc">{t('settings.developer.auth.desc')}</p>

      <div className="sgc-settings__actions sgc-dev__actions">
        <PillButton icon={PlusIcon} size="sm" onClick={() => setShowCreate(true)} disabled={!anyIntegration}>
          {t('settings.developer.auth.create')}
        </PillButton>
        {activeMcp > 0 && (
          <PillButton variant="secondary" icon={TrashIcon} size="sm" onClick={() => setClearMcp(true)}>
            {t('settings.developer.auth.clearMcp')}
          </PillButton>
        )}
      </div>
      {!anyIntegration && <p className="sgc-settings__row-desc">{t('settings.developer.auth.needIntegration')}</p>}
      {wantsCliToken && !cliReady && anyIntegration && <p className="sgc-settings__row-desc" role="status">{t('settings.developer.auth.needCliIntegration')}</p>}
      {notice && <p className="sgc-settings__row-desc" role="status">{notice}</p>}

      {error && (
        <p className="sgc-dev__error">
          {t('settings.developer.auth.loadError')}{' '}
          <button type="button" className="sgc-dev__link" onClick={load}>{t('settings.retry')}</button>
        </p>
      )}
      {!error && !data && <p className="sgc-settings__loading" role="status">{t('common.loading')}</p>}
      {data && data.tokens.length === 0 && <p className="sgc-settings__row-desc">{t('settings.developer.auth.empty')}</p>}

      {data && data.tokens.length > 0 && (
        <ul className="sgc-dev__tokens">
          {data.tokens.map((tok) => (
            <li key={tok.id} className={`sgc-dev__token-row ${tok.status !== 'active' || !tok.integrationEnabled ? 'sgc-dev__token-row--off' : ''}`} data-testid="token-row">
              <div className="sgc-dev__token-main">
                <div className="sgc-dev__token-head">
                  <strong className="sgc-dev__token-name">{tok.name}</strong>
                  <Chip label={t(`settings.developer.auth.clientName.${tok.clientKind}`)} />
                  <Chip label={statusLabel(tok)} selected={tok.status === 'active' && tok.integrationEnabled} />
                </div>
                <code className="sgc-dev__token-prefix">sgc_pat_{tok.prefix}…</code>
                <div className="sgc-dev__scope-chips">
                  {tok.scopes.map((s) => <code key={s} className="sgc-dev__scope-chip">{s}</code>)}
                </div>
                <dl className="sgc-dev__token-dates">
                  <div><dt>{t('settings.developer.auth.created')}</dt><dd>{formatDate(tok.createdAt)}</dd></div>
                  <div><dt>{t('settings.developer.auth.lastUsed')}</dt><dd>{formatDate(tok.lastUsedAt) || t('settings.developer.auth.never')}</dd></div>
                  <div><dt>{t('settings.developer.auth.expires')}</dt><dd>{formatDate(tok.expiresAt)}</dd></div>
                </dl>
              </div>
              {tok.status === 'active' && (
                <PillButton variant="secondary" size="sm" icon={TrashIcon} onClick={() => setToRevoke(tok)}>
                  {t('settings.developer.auth.revoke')}
                </PillButton>
              )}
            </li>
          ))}
        </ul>
      )}

      <CreateTokenModal
        open={showCreate}
        onClose={closeCreate}
        onCreated={load}
        integrations={integrations}
        meta={data}
        prefill={wantsCliToken && cliReady ? CLI_PREFILL : undefined}
      />
      <ConfirmModal
        open={!!toRevoke}
        title={t('settings.developer.auth.revokeTitle')}
        body={toRevoke ? t('settings.developer.auth.revokeBody', { name: toRevoke.name }) : ''}
        confirmLabel={t('settings.developer.auth.revoke')}
        onConfirm={revoke}
        onClose={() => setToRevoke(null)}
        busy={busy}
      />
      <ConfirmModal
        open={clearMcp}
        title={t('settings.developer.auth.clearMcpTitle')}
        body={t('settings.developer.auth.clearMcpBody')}
        confirmLabel={t('settings.developer.auth.clearMcp')}
        onConfirm={revokeMcp}
        onClose={() => setClearMcp(false)}
        busy={busy}
      />
    </Card>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Registro de actividad local
 * ---------------------------------------------------------------------------------------------- */
const KIND_KEYS = {
  fill: 'fill', fill_otp: 'fillOtp', copy_password: 'copyPassword', reveal_password: 'revealPassword', unlock: 'unlock',
  copy_secret: 'copySecret', reveal_secret: 'revealSecret', export_private_key: 'exportPrivateKey',
};

/** Une el registro del panel con el de la extension (null si no hay extension que conteste). Exportado para pruebas. */
export function mergeEvents(panelEvents, extensionEvents) {
  const all = [...(panelEvents || []), ...(Array.isArray(extensionEvents) ? extensionEvents : [])];
  return all
    .map((e, i) => ({ ...e, key: `${e.client}-${e.id ?? i}-${e.at}` }))
    .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

export function ActivityLogCard({ enabled, refreshKey }) {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const [events, setEvents] = useState(null);
  const [extensionSeen, setExtensionSeen] = useState(false);
  const [kind, setKind] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [panel, ext] = await Promise.all([
      activityLog.list().catch(() => []),
      extensionBridge.askExtension('SGC_EXT_ACTIVITY_LIST'),
    ]);
    setExtensionSeen(Array.isArray(ext?.events));
    setEvents(mergeEvents(panel, ext?.events));
    setLoading(false);
  }, []);
  useEffect(() => { if (enabled) load(); }, [enabled, load, refreshKey]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (events || []).filter((e) => (kind === 'all' || e.kind === kind)
      && (!q || `${e.itemName} ${e.origin}`.toLowerCase().includes(q)));
  }, [events, kind, query]);

  const exportJson = () => {
    const rows = shown.map(({ key, id, account, ...rest }) => rest); // sin ids internos ni la cuenta
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `securegate-activity-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (!enabled) return null;

  return (
    <Card className="sgc-settings__card" data-testid="activity-log">
      <h2 className="sgc-settings__card-title">{t('settings.developer.log.title')}</h2>
      <p className="sgc-settings__card-desc">{t('settings.developer.log.desc')}</p>

      <div className="sgc-dev__filters">
        <label className="sgc-dev__field">
          <span className="sgc-dev__field-label">{t('settings.developer.log.filterKind')}</span>
          <select className="sgc-settings__select" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="all">{t('settings.developer.log.all')}</option>
            {Object.entries(KIND_KEYS).map(([k, key]) => <option key={k} value={k}>{t(`settings.developer.log.kind.${key}`)}</option>)}
          </select>
        </label>
        <Input label={t('settings.developer.log.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      <div className="sgc-settings__actions sgc-dev__actions">
        <PillButton variant="secondary" size="sm" icon={RefreshIcon} onClick={load} loading={loading}>{t('settings.developer.log.refresh')}</PillButton>
        <PillButton variant="secondary" size="sm" icon={DownloadIcon} onClick={exportJson} disabled={shown.length === 0}>{t('settings.developer.log.export')}</PillButton>
      </div>

      {events && events.length === 0 && <p className="sgc-settings__row-desc">{t('settings.developer.log.empty')}</p>}
      {events && events.length > 0 && (
        <>
          <p className="sgc-settings__row-desc">{t('settings.developer.log.summary', { n: shown.length, max: activityLog.MAX_ENTRIES })}</p>
          <div className="sgc-dev__table-wrap">
            <table className="sgc-dev__table">
              <thead>
                <tr>
                  <th>{t('settings.developer.log.col.time')}</th>
                  <th>{t('settings.developer.log.col.event')}</th>
                  <th>{t('settings.developer.log.col.item')}</th>
                  <th>{t('settings.developer.log.col.where')}</th>
                  <th>{t('settings.developer.log.col.client')}</th>
                </tr>
              </thead>
              <tbody>
                {shown.slice(0, 200).map((e) => (
                  <tr key={e.key} data-testid="activity-row">
                    <td>{formatDate(e.at)}</td>
                    <td>{KIND_KEYS[e.kind] ? t(`settings.developer.log.kind.${KIND_KEYS[e.kind]}`) : e.kind}</td>
                    <td>{e.itemName || '—'}</td>
                    <td>{e.origin || '—'}</td>
                    <td>{e.client === 'extension' ? t('settings.developer.log.clientExtension') : t('settings.developer.log.clientPanel')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!extensionSeen && events && <p className="sgc-settings__row-desc">{t('settings.developer.log.extensionNote')}</p>}
    </Card>
  );
}

/** Confirmacion de apagar el registro (o el modo desarrollador con el registro encendido): lo borra aqui y en la extension. */
export function ClearLogModal({ open, onConfirm, onClose, busy }) {
  const { t } = useTranslation();
  return (
    <ConfirmModal
      open={open}
      title={t('settings.developer.log.clearTitle')}
      body={t('settings.developer.log.clearBody')}
      confirmLabel={t('settings.developer.log.turnOff')}
      onConfirm={onConfirm}
      onClose={onClose}
      busy={busy}
    />
  );
}

/** Borra el registro de este navegador y el de la extension. */
export async function clearAllActivity() {
  await activityLog.clear();
  await extensionBridge.askExtension('SGC_EXT_ACTIVITY_CLEAR');
}
